import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../../services/tokenService';
import { BACKEND_URL } from './constants';

export function useDOSMVP(userRole) {
  const [loading, _setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('simulateur');
  const [showProgrammeMode, setShowProgrammeMode] = useState(false);
  
  // Données du formulaire projet simple
  const [formData, setFormData] = useState({
    type_projet: 'individuel',
    territoire: 'guadeloupe',
    montant_travaux_ht: 150000,
    surface: 120,
    duree_chantier: 12,
    structure: 'beton',
    complexite_sol: 'simple',
    prevention_integree: false,
    audit_technique: false,
    part_ess_entreprises: 0,
    niveau_mutualisation: 'standard',
    raison_sociale: '',
    maitre_ouvrage: '',
    adresse_chantier: ''
  });
  
  // Données programme multi-lots
  const [programmeData, setProgrammeData] = useState({
    nom_programme: '',
    territoire: 'guadeloupe',
    maitre_ouvrage_nom: '',
    maitre_ouvrage_siret: '',
    maitre_ouvrage_adresse: '',
    maitre_ouvrage_email: '',
    lots: [
      { designation: '', type_lot: 'gros_oeuvre', montant_ht: 0, entreprise: '', siret: '', prevention: [], taux_contribution: 0.012 }
    ]
  });
  
  // Résultats
  const [result, setResult] = useState(null);
  const [programmeResult, setProgrammeResult] = useState(null);
  const [savedSimulation, setSavedSimulation] = useState(null);
  const [simulations, setSimulations] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [fondsDashboard, setFondsDashboard] = useState(null);
  const [decennaleDashboard, setDecennaleDashboard] = useState(null);
  
  // Modals
  const [showAttestationModal, setShowAttestationModal] = useState(false);
  const [attestationHtml, setAttestationHtml] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  
  const token = getToken();
  const headers = { Authorization: `Bearer ${token}` };
  
  // Calcul automatique (projets simples)
  const handleCalculate = useCallback(async () => {
    if (!formData.montant_travaux_ht || formData.montant_travaux_ht <= 0) return;
    
    setCalculating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/dos/calculate`,
        formData,
        { headers }
      );
      
      if (response.data.success) {
        setResult(response.data);
      }
    } catch {
    } finally {
      setCalculating(false);
    }
  }, [formData]);
  
  // Calcul automatique au changement des données
  useEffect(() => {
    if (formData.type_projet !== 'programme') {
      setShowProgrammeMode(false);
      const timer = setTimeout(() => {
        if (formData.montant_travaux_ht > 0) {
          handleCalculate();
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowProgrammeMode(true);
    }
  }, [formData, handleCalculate]);
  
  // Charger les simulations existantes
  const fetchSimulations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dos/simulations`, { headers });
      if (response.data.success) {
        setSimulations(response.data.simulations);
      }
    } catch {
    }
  };
  
  // Charger les programmes
  const fetchProgrammes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dos/v12/programmes`, { headers });
      if (response.data.success) {
        setProgrammes(response.data.programmes);
      }
    } catch {
    }
  };
  
  // Charger le dashboard du fonds
  const fetchFondsDashboard = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dos/fonds/dashboard`, { headers });
      if (response.data.success) {
        setFondsDashboard(response.data.dashboard);
      }
    } catch {
    }
  };
  
  // Charger le dashboard décennale
  const fetchDecennaleDashboard = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dos/v12/decennale/dashboard`, { headers });
      if (response.data.success) {
        setDecennaleDashboard(response.data.dashboard);
      }
    } catch {
    }
  };
  
  useEffect(() => {
    fetchSimulations();
    fetchProgrammes();
    if (userRole === 'super_admin' || userRole === 'admin') {
      fetchFondsDashboard();
      fetchDecennaleDashboard();
    }
  }, [userRole]);
  
  // Sauvegarder simulation simple
  const handleSave = async () => {
    if (!result) {
      toast.error('Veuillez d\'abord effectuer un calcul');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/dos/simulation`,
        { projet: result.projet, calcul: result.calcul },
        { headers }
      );
      
      if (response.data.success) {
        setSavedSimulation(response.data.simulation);
        toast.success('Simulation sauvegardée avec succès');
        fetchSimulations();
      }
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  // Créer programme multi-lots
  const handleCreateProgramme = async () => {
    if (!programmeData.nom_programme || programmeData.lots.length === 0) {
      toast.error('Veuillez renseigner le nom du programme et au moins un lot');
      return;
    }
    
    const totalLots = programmeData.lots.reduce((sum, lot) => sum + (lot.montant_ht || 0), 0);
    if (totalLots <= 0) {
      toast.error('Le montant total des lots doit être supérieur à 0');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/dos/v12/programme`,
        programmeData,
        { headers }
      );
      
      if (response.data.success) {
        setProgrammeResult(response.data);
        toast.success('Programme créé avec succès');
        fetchProgrammes();
      } else {
        toast.error(response.data.error || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du programme');
    } finally {
      setSaving(false);
    }
  };
  
  // Ajouter un lot
  const addLot = () => {
    setProgrammeData(prev => ({
      ...prev,
      lots: [...prev.lots, { designation: '', type_lot: 'second_oeuvre', montant_ht: 0, entreprise: '', siret: '', prevention: [], taux_contribution: 0.012 }]
    }));
  };
  
  // Supprimer un lot
  const removeLot = (index) => {
    if (programmeData.lots.length > 1) {
      setProgrammeData(prev => ({
        ...prev,
        lots: prev.lots.filter((_, i) => i !== index)
      }));
    }
  };
  
  // Mettre à jour un lot
  const updateLot = (index, updatedLot) => {
    setProgrammeData(prev => ({
      ...prev,
      lots: prev.lots.map((lot, i) => i === index ? updatedLot : lot)
    }));
  };
  
  // Voir l'attestation
  const handleViewAttestation = async (simulationId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/dos/attestation/${simulationId}/html`,
        { headers }
      );
      setAttestationHtml(response.data);
      setShowAttestationModal(true);
    } catch {
      toast.error('Erreur lors du chargement de l\'attestation');
    }
  };
  
  // Télécharger l'attestation PDF
  const handleDownloadPDF = async (simulationId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/dos/attestation/${simulationId}/pdf`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Attestation_DOS_${simulationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Attestation PDF téléchargée');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };
  
  // Mise à jour du formulaire
  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Calculer le total des lots
  const totalLots = programmeData.lots.reduce((sum, lot) => sum + (lot.montant_ht || 0), 0);
  return { loading, calculating, saving, activeTab, setActiveTab, showProgrammeMode, formData, setFormData, programmeData, setProgrammeData, result, programmeResult, savedSimulation, simulations, programmes, fondsDashboard, decennaleDashboard, showAttestationModal, setShowAttestationModal, attestationHtml, setAttestationHtml, showSignatureModal, setShowSignatureModal, handleCalculate, fetchSimulations, fetchProgrammes, fetchFondsDashboard, fetchDecennaleDashboard, handleSave, handleCreateProgramme, addLot, removeLot, updateLot, handleViewAttestation, handleDownloadPDF, updateForm, totalLots };
}
